import React, { useEffect, useMemo } from 'react';
import { UsersAnnualLeaveHeader } from "./components/UsersAnnualLeaveHeader";
import { YearContainer } from "./components/YearContainer";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { selectCalendarDate } from '@shared/store/dashboard.duck';
import { usePageName } from '@shared/hooks/UsePageName';
import { desktopSmall } from '@shared/ui/atoms/MediaQueries';
import {
  loadUsersHolidays,
  selectCompanyHolidays,
  selectHasHolidayLoadError,
  selectHolidayLoadErrorMessage,
  selectIsLoading,
  selectUsersBookedHolidays, selectUsersCustomLeave, selectUsersCustomNonWorkingDay, selectUsersNonWorkingDay
} from '@features/holidays/store/holidays-v2.duck';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { HolidaysTakenOverview } from "./components/HolidaysTakenOverview";
import { HolidaysBookingsList } from "./components/HolidaysBookingsList";
import { Sidebar } from '@shared/ui/atoms/Sidebar';
import { BiggerThanTabletOnly } from '@shared/ui/atoms/DeviceSpecific';
import { WorkPlannerLayout } from '@shared/ui/molecules/work-planner/WorkPlannerLayout';
import { useParams } from "react-router";
import { selectConfig, selectHasGlobalConfigLoaded } from '@shared/store/config.duck';

export function UserAnnualHolidayView(props: Props) {
  const dispatch = useDispatch();
  const calendarDate = useSelector(selectCalendarDate);
  const config = useSelector(selectConfig);
  const hasGlobalConfigLoaded = useSelector(selectHasGlobalConfigLoaded);
  const holidays = useSelector(selectUsersBookedHolidays);
  const customLeave = useSelector(selectUsersCustomLeave);
  const nonWorkingDays = useSelector(selectUsersNonWorkingDay);
  const customNonWorkingDays = useSelector(selectUsersCustomNonWorkingDay);
  const companyHolidays = useSelector(selectCompanyHolidays);
  const hasLoadError = useSelector(selectHasHolidayLoadError);
  const loadErrorMessage = useSelector(selectHolidayLoadErrorMessage);
  const isLoading = useSelector(selectIsLoading);
  const params = useParams<any>();
  const userId = params.userId;

  usePageName('Team Today - Your holidays');

  const holidayYearStartDate = useMemo(() => {
    if (config && calendarDate && hasGlobalConfigLoaded) {
      return config.getHolidayYearStartDate(calendarDate)
    }
  }, [calendarDate, config, hasGlobalConfigLoaded]);

  useEffect(() => {
    if (holidayYearStartDate && hasGlobalConfigLoaded) {
      dispatch(loadUsersHolidays({holidayYear: holidayYearStartDate.clone().add(5, 'days'), userId: userId}));
    }
  }, [config, dispatch, hasGlobalConfigLoaded, holidayYearStartDate, userId]);

  if (!hasGlobalConfigLoaded) {
    return null;
  }

  return (
    <WorkPlannerLayout pageContentStyles={{paddingTop: 24}} numberOfWeeksInMonth={6}>
      {!isLoading && <UsersAnnualLeaveHeader />}
      {isLoading
        ? <LoadingWrapper>
            <LoadingSpinner fullScreen={false} hideBorder={true} />
          </LoadingWrapper>
        : hasLoadError
        ? <ErrorState>
            <BodyRegular>{loadErrorMessage || 'Something went wrong fetching this calendar. Please refresh the page or try again later.'}</BodyRegular>
          </ErrorState>
        : <Content>
            <Sidebar>
              <HolidaysTakenOverview />
              <HolidaysBookingsList />
            </Sidebar>
            <BiggerThanTabletOnly>
              {holidayYearStartDate && <YearContainer holidaysBooked={holidays}
                                                      companyHolidays={companyHolidays}
                                                      nonWorkingDays={nonWorkingDays}
                                                      customLeaveDays={customLeave}
                                                      customNonWorkingDays={customNonWorkingDays}
                                                      startDate={holidayYearStartDate} />}
            </BiggerThanTabletOnly>
          </Content>}
    </WorkPlannerLayout>
  )
}

interface Props {
}

const Content = styled.div`
  margin: 24px 0 0 0;
  display: flex;
  flex-direction: column-reverse;

  @media (${desktopSmall}) {
    flex-direction: row;
  }
`

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 64px 24px;
`

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 8px;
  width: 100%;
  padding: 64px 24px;
`
